import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { invalidateReportTemplates } from '@futrli/views/mutations/invalidateListingQueries';
import { Logger } from '@futrli/shared-services';
import { ReportTemplateDetail, ReportTemplateRequest } from '@futrli-sdk/predict';

import { createContext } from '../core/context';
import {
  fetchReportTemplateById,
  fetchReportTemplates,
  patchReportTemplate
} from '../core/api/reporting/report-templates';
import { getItemOwnerName } from './utils-data';
import { PredictManagementContext } from '../predict-management/context/predict-management-context';
import { ReportTemplate } from './types';
import { useMembersContext } from './members-context';
import { usePredictionsInfoContext } from '../forecast/detail/context/predictions-info-context';

interface Props {
  children: ReactNode;
}
interface TemplatesContextProps {
  templates: ReportTemplate[];
  workspaceTemplates: ReportTemplate[];
  onboardingTemplates: ReportTemplate[];
  fetchTemplateById: (id: string) => Promise<ReportTemplateDetail | undefined>;
  patchTemplate: (id: string, data: Partial<ReportTemplateRequest>, isWorkspaceTemplate?: boolean) => Promise<void>;
  isFetchingTemplates: boolean;
  getOwnerName: (id: string) => string;
}

const TemplatesContext = createContext<TemplatesContextProps>();

function useContextSetup() {
  const { members } = useMembersContext();
  const { addErrorMessage } = useContext(PredictManagementContext);
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();
  const organisationId = tempOrganisationId as string;
  const workspaceId = tempWorkspaceId as string;
  const { isConsolidation } = usePredictionsInfoContext();

  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [onboardingTemplates, setOnboardingTemplates] = useState<ReportTemplate[]>([]);
  const [workspaceTemplates, setWorkspaceTemplates] = useState<ReportTemplate[]>([]);
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [isPatchingTemplate, setIsPatchingTemplate] = useState(false);

  // Fetch API data
  useEffect(() => {
    fetchTemplates();
  }, [workspaceId, organisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = async () => {
    try {
      if (!isFetchingTemplates) {
        setIsFetchingTemplates(true);
        const fetchedTemplates = await fetchReportTemplates(workspaceId, organisationId, isConsolidation);
        setTemplates(fetchedTemplates.filter(t => t.kind === 'futrli' && t.available_area !== 'onboarding'));
        setWorkspaceTemplates(
          fetchedTemplates.filter(t => t.kind === 'workspace' && t.available_area !== 'onboarding')
        );
        setOnboardingTemplates(fetchedTemplates.filter(t => t.available_area === 'onboarding'));
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to fetch Templates'));
      Logger.error(e as string);
    } finally {
      setIsFetchingTemplates(false);
    }
  };

  const fetchTemplateById = async (id: string) => {
    try {
      const template = await fetchReportTemplateById(workspaceId, organisationId, id, isConsolidation);
      return template;
    } catch (e) {
      addErrorMessage?.(new Error('Failure to fetch Template by id'));
      Logger.error(e as string);
    }
  };

  const patchTemplate = async (id: string, data: Partial<ReportTemplateRequest>, isWorkspaceTemplate?: boolean) => {
    try {
      if (!isPatchingTemplate) {
        setIsPatchingTemplate(true);
        await patchReportTemplate(workspaceId, organisationId, id, data, isConsolidation);
        if (isWorkspaceTemplate) {
          const patchedTemplate = workspaceTemplates.find(r => r.id === id);
          if (patchedTemplate) {
            setWorkspaceTemplates([...workspaceTemplates.filter(r => r.id !== id), { ...patchedTemplate, ...data }]);
          }
        } else {
          const patchedTemplate = templates.find(r => r.id === id);
          if (patchedTemplate) {
            setTemplates([...templates.filter(r => r.id !== id), { ...patchedTemplate, ...data }]);
          }
        }
        invalidateReportTemplates(organisationId, workspaceId, isConsolidation);
      }
    } catch (e) {
      addErrorMessage?.(new Error('Failure to patch Template'));
      Logger.error(e as string);
    } finally {
      setIsPatchingTemplate(false);
    }
  };

  const getOwnerName = (id: string) => {
    return getItemOwnerName(
      workspaceTemplates?.find(r => r.id === id),
      members
    );
  };

  return {
    templates,
    workspaceTemplates,
    onboardingTemplates,
    fetchTemplateById,
    patchTemplate,
    isFetchingTemplates,
    getOwnerName
  };
}

export function TemplatesContextProvider({ children }: Props) {
  const value = useContextSetup();

  return <TemplatesContext.Provider value={value}>{children}</TemplatesContext.Provider>;
}

export const useTemplatesContext = TemplatesContext.useContext;
